import React, { useState, useEffect, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  AlertTitle,
  Box,
  Typography,
  Button,
  LinearProgress,
  Drawer,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  getByServiceType,
  getByServiceTypeAndIntakeFormType,
  FieldMapping,
  getVisibleSections,
} from '../../services/fieldMapping.service';

// Import all intake form section components
import PersonalInformation from './PersonalInformation';
import LegalIssues from './LegalIssues';
import EducationTraining from './EducationTraining';
import WorkHistory from './WorkHistory';
import PreviousEmploymentHistory from './PreviousEmploymentHistory';
import EmploymentPostInjury from './EmploymentPostInjury';
import JobSearchInformation from './JobSearchInformation';
import BenefitsInformation from './BenefitsInformation';
import MedicalHistory from './MedicalHistory';
import TherapyInformation from './TherapyInformation';
import ActivitiesDailyLiving from './ActivitiesDailyLiving';
import HospitalizationInformation from './HospitalizationInformation';
import Signatures from './Signatures';

interface DynamicFieldLoaderProps {
  serviceTypeId?: number;
  intakeFormTypeId?: number;
  formData: any;
  onFormDataChange: (data: any) => void;
  showAllSections?: boolean;
  onSubmit?: () => void;
  initialSection?: string | null;
  readOnly?: boolean;
  saving?: boolean;
}

interface SectionComponent {
  [key: string]: React.ComponentType<any>;
}

const SECTION_COMPONENTS: SectionComponent = {
  personal_info: PersonalInformation,
  legal_issues: LegalIssues,
  education_training: EducationTraining,
  work_history: WorkHistory,
  previous_employment_history: PreviousEmploymentHistory,
  employment_post_injury: EmploymentPostInjury,
  job_search_information: JobSearchInformation,
  benefits_information: BenefitsInformation,
  medical_history: MedicalHistory,
  therapy_information: TherapyInformation,
  activities_daily_living: ActivitiesDailyLiving,
  hospitalization_information: HospitalizationInformation,
  signatures: Signatures,
};

const DEFAULT_SECTIONS = [
  'personal_info',
  'legal_issues',
  'education_training',
  'work_history',
  'previous_employment_history',
  'employment_post_injury',
  'job_search_information',
  'benefits_information',
  'medical_history',
  'therapy_information',
  'activities_daily_living',
  'signatures',
];

const SECTION_LABELS: Record<string, string> = {
  personal_info: 'Personal Info',
  legal_issues: 'Legal Issues',
  education_training: 'Education & Training',
  work_history: 'Work History',
  previous_employment_history: 'Previous Employment',
  employment_post_injury: 'Employment Post Injury',
  job_search_information: 'Job Search',
  benefits_information: 'Benefits Information',
  medical_history: 'Medical History',
  therapy_information: 'Therapy Information',
  activities_daily_living: 'Daily Living',
  hospitalization_information: 'Hospitalization',
  signatures: 'Signatures',
};

const SECTION_ICONS: Record<string, string> = {
  personal_info: '👤',
  legal_issues: '⚖️',
  education_training: '🎓',
  work_history: '💼',
  previous_employment_history: '🗂️',
  employment_post_injury: '🏥',
  job_search_information: '🔍',
  benefits_information: '💰',
  medical_history: '🩺',
  therapy_information: '🧠',
  activities_daily_living: '🏃',
  hospitalization_information: '🏨',
  signatures: '✍️',
};

const SIDEBAR_WIDTH = 268;

const DynamicFieldLoader: React.FC<DynamicFieldLoaderProps> = ({
  serviceTypeId,
  intakeFormTypeId,
  formData,
  onFormDataChange,
  showAllSections = false,
  onSubmit,
  initialSection = null,
  readOnly = false,
  saving = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const [sectionsVisibilityLoaded, setSectionsVisibilityLoaded] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    loadFieldMappings();
    if (intakeFormTypeId) {
      loadSectionVisibility();
    } else {
      setSectionsVisibilityLoaded(true);
    }
  }, [serviceTypeId, intakeFormTypeId]);

  const sectionsToRender = useMemo(() => {
    const sectionsWithMappings = new Map<string, FieldMapping[]>();

    if (fieldMappings && Array.isArray(fieldMappings)) {
      fieldMappings.forEach(mapping => {
        if (!sectionsWithMappings.has(mapping.section)) {
          sectionsWithMappings.set(mapping.section, []);
        }
        sectionsWithMappings.get(mapping.section)!.push(mapping);
      });
    }

    const filterHidden = (sections: string[]) =>
      sections.filter(s => !hiddenSections.has(s));

    if (showAllSections || sectionsWithMappings.size === 0) {
      return filterHidden(DEFAULT_SECTIONS).map(section => ({
        name: section,
        mappings: sectionsWithMappings.get(section) || [],
        hasMappings: sectionsWithMappings.has(section),
      }));
    }

    return Array.from(sectionsWithMappings.entries())
      .filter(([section]) => !hiddenSections.has(section))
      .map(([section, mappings]) => ({ name: section, mappings, hasMappings: true }));
  }, [fieldMappings, hiddenSections, showAllSections, sectionsVisibilityLoaded]);

  useEffect(() => {
    if (!initialized && !loading && sectionsVisibilityLoaded && initialSection) {
      const idx = sectionsToRender.findIndex(s => s.name === initialSection);
      if (idx !== -1) setActiveStep(idx);
      setInitialized(true);
    }
  }, [loading, initialSection, initialized, sectionsVisibilityLoaded, sectionsToRender]);

  useEffect(() => {
    if (sectionsVisibilityLoaded && sectionsToRender.length > 0 && activeStep >= sectionsToRender.length) {
      setActiveStep(0);
    }
  }, [sectionsToRender.length, sectionsVisibilityLoaded, activeStep]);

  const loadSectionVisibility = async () => {
    if (!intakeFormTypeId) { setSectionsVisibilityLoaded(true); return; }
    try {
      const response = await getVisibleSections(intakeFormTypeId);
      const visibleSections = response.data.data || [];
      const hidden = DEFAULT_SECTIONS.filter(s => !visibleSections.includes(s));
      setHiddenSections(new Set(hidden));
    } catch {
      setHiddenSections(new Set());
    } finally {
      setSectionsVisibilityLoaded(true);
    }
  };

  const loadFieldMappings = async () => {
    if (!serviceTypeId) { setFieldMappings([]); return; }
    setLoading(true);
    setError(null);
    try {
      const response = intakeFormTypeId
        ? await getByServiceTypeAndIntakeFormType(serviceTypeId, intakeFormTypeId)
        : await getByServiceType(serviceTypeId);
      const mappingsData = response.data?.data;
      if (Array.isArray(mappingsData)) {
        setFieldMappings(mappingsData);
      } else {
        setFieldMappings([]);
        setError('Invalid field mappings data received');
      }
    } catch {
      setError('Failed to load form fields. Using default fields.');
      setFieldMappings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionDataChange = (sectionName: string, sectionData: any) => {
    onFormDataChange({ ...formData, [sectionName]: { ...formData[sectionName], ...sectionData } });
    // Clear validation errors as the user fills in fields
    if (validationErrors.length > 0) setValidationErrors([]);
  };

  const navigateTo = (step: number) => {
    setValidationErrors([]);
    setActiveStep(step);
    if (step >= 0 && step < sectionsToRender.length) {
      onFormDataChange({ ...formData, current_section: sectionsToRender[step].name });
    }
    setMobileDrawerOpen(false);
    const el = document.getElementById('intake-content-area');
    if (el) el.scrollTop = 0;
  };

  /**
   * Validate required fields in the current section.
   * Returns an array of human-readable field labels that are missing.
   */
  const validateCurrentSection = (): string[] => {
    const section = sectionsToRender[activeStep];
    if (!section || section.mappings.length === 0) return [];

    const sectionData = formData[section.name] || {};
    const missing: string[] = [];

    section.mappings
      .filter(m => m.is_required && !m.is_hidden)
      .forEach(m => {
        // form_field format: "section_name.field_name"
        const fieldName = m.form_field.includes('.')
          ? m.form_field.split('.').slice(1).join('.')
          : m.form_field;

        const value = sectionData[fieldName];
        const isEmpty =
          value === null ||
          value === undefined ||
          value === '' ||
          value === false;

        if (isEmpty) {
          const label = m.label ||
            fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          missing.push(label);
        }
      });

    return missing;
  };

  /** Called by the "Next Section" button — validates before advancing. */
  const handleNextWithValidation = () => {
    const missing = validateCurrentSection();
    if (missing.length > 0) {
      setValidationErrors(missing);
      const el = document.getElementById('intake-content-area');
      if (el) el.scrollTop = 0;
      return;
    }
    navigateTo(activeStep + 1);
  };

  const isSectionComplete = (sectionName: string): boolean => {
    const d = formData[sectionName];
    if (!d || typeof d !== 'object') return false;
    return Object.values(d).some(
      v => v !== null && v !== undefined && v !== '' && v !== false
    );
  };

  const completedCount = sectionsToRender.filter(s => isSectionComplete(s.name)).length;
  const progressPercent = sectionsToRender.length
    ? Math.round((completedCount / sectionsToRender.length) * 100)
    : 0;

  // ─── Loading / error guards ───────────────────────────────────────────────
  if (loading || (intakeFormTypeId && !sectionsVisibilityLoaded)) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='300px'>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !Array.isArray(fieldMappings)) {
    return <Alert severity='error'>{error}</Alert>;
  }

  // ─── Sidebar content ──────────────────────────────────────────────────────
  const SidebarContent = () => (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8f9fc',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Progress header */}
      <Box sx={{ px: 2.5, pt: 3, pb: 2 }}>
        <Typography variant='subtitle2' fontWeight={700} color='text.secondary' sx={{ mb: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Your Progress
        </Typography>
        <Box display='flex' alignItems='center' gap={1} mb={1}>
          <LinearProgress
            variant='determinate'
            value={progressPercent}
            sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
          />
          <Typography variant='caption' fontWeight={600} color='primary.main' sx={{ minWidth: 36 }}>
            {progressPercent}%
          </Typography>
        </Box>
        <Typography variant='caption' color='text.secondary'>
          {completedCount} of {sectionsToRender.length} sections filled
        </Typography>
      </Box>

      <Divider />

      {/* Section list */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {sectionsToRender.map((section, index) => {
          const isActive = index === activeStep;
          const isComplete = isSectionComplete(section.name);
          const label = SECTION_LABELS[section.name] || section.name.replace(/_/g, ' ');
          const icon = SECTION_ICONS[section.name] || '📋';

          return (
            <Box
              key={section.name}
              onClick={() => navigateTo(index)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.2,
                cursor: 'pointer',
                mx: 1,
                borderRadius: 2,
                transition: 'all 0.15s ease',
                bgcolor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'white' : 'text.primary',
                '&:hover': {
                  bgcolor: isActive ? 'primary.dark' : 'grey.100',
                },
              }}
            >
              {/* Step number / status icon */}
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28 }}>
                {isActive ? (
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'white', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                    {index + 1}
                  </Box>
                ) : isComplete ? (
                  <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main' }} />
                ) : (
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>
                    {index + 1}
                  </Box>
                )}
              </Box>

              {/* Emoji icon */}
              <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{icon}</Typography>

              {/* Label */}
              <Typography
                variant='body2'
                sx={{
                  flex: 1,
                  fontWeight: isActive ? 600 : isComplete ? 500 : 400,
                  fontSize: '0.82rem',
                  lineHeight: 1.3,
                  color: isActive ? 'white' : isComplete ? 'text.primary' : 'text.secondary',
                }}
              >
                {label}
              </Typography>

              {/* Complete badge for non-active */}
              {isComplete && !isActive && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Auto-save status */}
      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box display='flex' alignItems='center' gap={0.8}>
          <SaveIcon sx={{ fontSize: 14, color: saving ? 'warning.main' : 'success.main' }} />
          <Typography variant='caption' color={saving ? 'warning.main' : 'text.secondary'}>
            {saving ? 'Saving…' : 'Auto-saved'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // ─── Current section content ──────────────────────────────────────────────
  const currentSection = sectionsToRender[activeStep];
  const isFinalStep = activeStep === sectionsToRender.length - 1;
  const Component = currentSection ? SECTION_COMPONENTS[currentSection.name] : null;
  const currentLabel = currentSection
    ? (SECTION_LABELS[currentSection.name] || currentSection.name.replace(/_/g, ' '))
    : '';

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: '80vh', position: 'relative' }}>
      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}>
          <SidebarContent />
        </Box>
      )}

      {/* ── Mobile sidebar drawer ── */}
      {isMobile && (
        <Drawer
          anchor='left'
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
        >
          <SidebarContent />
        </Drawer>
      )}

      {/* ── Main content ── */}
      <Box
        id='intake-content-area'
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isMobile && (
            <Box
              component='button'
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', p: 0 }}
            >
              <MenuIcon sx={{ color: 'text.secondary' }} />
            </Box>
          )}
          <Box>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', fontWeight: 600 }}>
              Step {activeStep + 1} of {sectionsToRender.length}
            </Typography>
            <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {SECTION_ICONS[currentSection?.name || ''] || '📋'}&nbsp;{currentLabel}
            </Typography>
          </Box>

          {/* Mobile progress */}
          {isMobile && (
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant='caption' color='primary.main' fontWeight={700}>
                {progressPercent}%
              </Typography>
              <LinearProgress
                variant='determinate'
                value={progressPercent}
                sx={{ width: 80, height: 6, borderRadius: 3, mt: 0.5 }}
              />
            </Box>
          )}
        </Box>

        {/* Validation error banner */}
        {validationErrors.length > 0 && (
          <Alert
            severity='error'
            sx={{ mx: { xs: 2, md: 4 }, mt: 2, borderRadius: 2 }}
            onClose={() => setValidationErrors([])}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              Please fill in the required fields before continuing:
            </AlertTitle>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              {validationErrors.map(label => (
                <li key={label}>{label}</li>
              ))}
            </Box>
          </Alert>
        )}

        {/* Section form content */}
        <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3 }}>
          {Component ? (
            <Component
              formData={formData[currentSection.name] || {}}
              onFormDataChange={
                readOnly
                  ? () => undefined
                  : (data: any) => handleSectionDataChange(currentSection.name, data)
              }
              onChange={
                readOnly
                  ? () => undefined
                  : (data: any) => handleSectionDataChange(currentSection.name, data)
              }
              data={formData[currentSection.name] || {}}
              fieldMappings={currentSection.mappings}
              isRequired={currentSection.mappings.some(m => m.is_required && !m.is_hidden)}
              onSubmit={isFinalStep && !readOnly ? onSubmit : undefined}
              readOnly={readOnly}
            />
          ) : (
            <Alert severity='warning'>Section not found.</Alert>
          )}
        </Box>

        {/* Bottom navigation bar */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Button
            variant='outlined'
            startIcon={<ChevronLeftIcon />}
            disabled={activeStep === 0}
            onClick={() => navigateTo(activeStep - 1)}
            sx={{ minWidth: 120 }}
          >
            Back
          </Button>

          <Typography variant='caption' color='text.secondary' sx={{ display: { xs: 'none', sm: 'block' } }}>
            {isSectionComplete(currentSection?.name || '') ? '✅ Section saved' : ''}
          </Typography>

          {isFinalStep ? (
            !readOnly && (
              <Button
                variant='contained'
                color='success'
                onClick={onSubmit}
                disabled={!onSubmit}
                sx={{ minWidth: 150 }}
              >
                Submit Form ✓
              </Button>
            )
          ) : (
            <Button
              variant='contained'
              endIcon={<ChevronRightIcon />}
              onClick={handleNextWithValidation}
              sx={{ minWidth: 150 }}
            >
              Next Section
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicFieldLoader;
